/**
 * MessageClarification — the send-wiring around ``<ClarificationNote>``.
 *
 * Mounted by ``<Messages>`` under any agent turn carrying
 * ``ChatMessageView.clarification``. Splitting the transport out of the card
 * keeps ``@ando/ui`` free of chat-store knowledge (the same split
 * ``SourceConfirmationSurface`` uses for ``<SourceConfirmationCard>``), so
 * the visual component stays unit-testable without a WS client.
 *
 * Click semantics — INLINE, not a pending action:
 *   The clarification rides a turn that already COMPLETED. Nothing is blocked
 *   BE-side, so there is no confirmation endpoint to call and no Temporal fork
 *   to release. Picking an option is literally the user asking a sharper
 *   question, so we send ``suggested_query`` through the ordinary
 *   ``useSendQuery`` path — it lands in the history as a real user turn, which
 *   is exactly what the user just did.
 *
 * ``focus: "structured"`` override:
 *   Clarifications are only ever born in the enterprise/SQL pipeline. Sending
 *   the follow-up on the conversation's ambient focus would let a
 *   ``standard`` thread route the retry away from the connector that asked the
 *   question — the user would click "Importo Lordo" and get RAG prose back.
 *   Pinning the focus mirrors V1 (``ClarificationNeededDisplay`` passed
 *   ``focus="structured"`` on every click).
 */

import { ClarificationNote } from "@ando/ui";
import type * as React from "react";

import type { ClarificationNeededDisplay } from "@ando/core/domain";
import { useSendQuery } from "../hooks/useSendQuery";

export interface MessageClarificationProps {
  readonly payload: ClarificationNeededDisplay;
  readonly selectedQuery?: string;
}

export const MessageClarification: React.FC<MessageClarificationProps> = ({ payload, selectedQuery }) => {
  const { send } = useSendQuery();

  return (
    <ClarificationNote
      questions={payload.questions}
      error={payload.error ?? null}
      selectedQuery={selectedQuery}
      onSelect={(suggestedQuery) => {
        void send({
          text: suggestedQuery,
          configOverride: { focus: "structured" },
        });
      }}
    />
  );
};

MessageClarification.displayName = "MessageClarification";
